import { useState } from 'react';
import {
    Combobox,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
} from '@headlessui/react';
import { twMerge } from 'tailwind-merge';

import { useAppSelector } from 'flow/hooks/useRedux';
import useAppContext from 'flow/hooks/useAppContext';

import ContactAvatar from 'flow/components/ui-components/ContactAvatar';
import ParticipantPill from './ParticipantPill';
import useParticipantCandidates from 'flow/hooks/useParticipantCandidates';

import { getExtensions } from 'flow/selectors/ExtensionSelectors';
import { extractName } from 'flow/utils';

import { ContactFull } from 'flow/types/Contact';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';

interface Props {
    /**
     * Names the field for assistive tech. The visible label sits outside this
     * component, so only the caller can say what this combobox is for.
     */
    ariaLabel?: string;
    selectedKeys: ExtensionKey[];
    onChange: (next: ExtensionKey[]) => void;
    ownKey: ExtensionKey | undefined;
}

const ParticipantPicker = ({
    selectedKeys,
    onChange,
    ownKey,
    ariaLabel,
}: Props) => {
    const { i18next } = useAppContext();
    const extensions = useAppSelector(getExtensions);

    const [query, setQuery] = useState('');

    const filteredKeys = useParticipantCandidates(ownKey, selectedKeys, query);

    const removeKey = (key: ExtensionKey) =>
        onChange(selectedKeys.filter((k) => k !== key));

    return (
        <Combobox
            value={null}
            onChange={(key: ExtensionKey | null) => {
                if (!key) return;
                onChange([...selectedKeys, key]);
                setQuery('');
            }}
            onClose={() => setQuery('')}
            immediate={true}
            virtual={
                filteredKeys.length > 0 ? { options: filteredKeys } : undefined
            }
        >
            <div className="relative">
                <div className="flex flex-col rounded-sm bg-gray-100 p-2 text-sm dark:bg-gray-100">
                    {selectedKeys.length > 0 && (
                        // Reaches 4px past the start and top and pads itself
                        // by as much, so the pills stay put and the focus
                        // ring of their remove buttons fits in the scroller.
                        <div className="scrollbar -ms-1 -mt-1 flex max-h-42 flex-wrap gap-2 overflow-x-hidden overflow-y-auto p-1">
                            {selectedKeys.map((key) => (
                                <ParticipantPill
                                    key={key}
                                    extensionKey={key}
                                    onRemove={removeKey}
                                />
                            ))}
                        </div>
                    )}
                    <ComboboxInput
                        aria-label={ariaLabel}
                        className="w-full bg-transparent py-0 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-hidden"
                        autoComplete="off"
                        displayValue={() => query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                            if (
                                e.key === 'Backspace' &&
                                query === '' &&
                                selectedKeys.length > 0
                            ) {
                                e.preventDefault();
                                removeKey(
                                    selectedKeys[selectedKeys.length - 1],
                                );
                            }
                        }}
                    />
                </div>

                <ComboboxOptions
                    anchor={{ to: 'bottom start', gap: 8, padding: 8 }}
                    modal={false}
                    className="scrollbar scrollbar-gutter-stable shadow-base z-50 w-(--input-width) overflow-auto rounded-md bg-white py-1 dark:bg-gray-300"
                >
                    {filteredKeys.length === 0 ? (
                        <div className="px-3 py-2 text-sm text-gray-500">
                            {i18next.t('General.Search.Empty.Title')}
                        </div>
                    ) : (
                        ({ option }) => {
                            const ext = extensions.get(option);
                            const contact = ext.get(
                                'contact',
                            ) as TypedMap<ContactFull>;
                            return (
                                <ComboboxOption
                                    value={option}
                                    className={({ focus }) =>
                                        twMerge(
                                            'flex w-full cursor-pointer items-center gap-x-2 px-3 py-2 text-sm text-gray-800',
                                            focus &&
                                                'bg-gray-200 dark:bg-gray-400',
                                        )
                                    }
                                >
                                    <ContactAvatar
                                        contact={contact}
                                        lazyLoad={true}
                                    />
                                    <span className="truncate">
                                        {extractName(contact)}
                                    </span>
                                </ComboboxOption>
                            );
                        }
                    )}
                </ComboboxOptions>
            </div>
        </Combobox>
    );
};

export default ParticipantPicker;
